import React, { useEffect, useRef, useState } from 'react';
import { useTenants } from '../../contexts/TenantContext';
import { useAuth } from '../../hooks';
import { findSite, sitesForTenant } from '../../utils/sites';
import { roleLabel } from '../../utils/userDisplay';
import { SessionSwitchPanel, sessionContextLabel } from './SessionSwitchPanel';

const CHEVRON = 'M19 9l-7 7-7-7';

function useSessionChoices() {
  const { accountType, availableRoles, switchRole } = useAuth();
  const { switchableTenants, currentTenantId, currentTenant, currentSiteId, switching, error } = useTenants();
  const sites = sitesForTenant(switchableTenants, currentTenantId);
  const siteName = sites.length > 1
    ? findSite(switchableTenants.find(tenant => tenant.id === currentTenantId), currentSiteId)?.name
    : null;
  const organisations = currentTenant && !switchableTenants.some(tenant => tenant.id === currentTenant.id)
    ? [currentTenant, ...switchableTenants]
    : switchableTenants;

  return {
    accountType,
    availableRoles,
    switchRole,
    switching,
    role: roleLabel(accountType),
    tenantName: currentTenant?.name ?? null,
    siteName: siteName ?? null,
    showRoles: availableRoles.length > 1,
    showPlaces: organisations.length > 1 || sites.length > 1,
    error,
  };
}

function RoleToggle() {
  const { availableRoles, accountType, switchRole } = useSessionChoices();
  return (
    <div
      className="inline-flex rounded-lg bg-gray-100 p-0.5"
      role="group"
      aria-label="Switch role"
    >
      {availableRoles.map(role => {
        const selected = role === accountType;
        return (
          <button
            key={role}
            type="button"
            aria-pressed={selected}
            onClick={() => switchRole(role)}
            className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
              selected ? 'bg-brand-600 text-white shadow-sm' : 'text-gray-600 hover:text-brand-800'
            }`}
          >
            {roleLabel(role)}
          </button>
        );
      })}
    </div>
  );
}

function PlaceMenu({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const { role, tenantName, siteName, switching } = useSessionChoices();
  const label = [tenantName, siteName].filter(Boolean).join(' · ') || 'Organisation';

  return (
    <div className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        disabled={switching}
        onClick={onToggle}
        className="inline-flex max-w-[16rem] items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-60"
      >
        <span className="truncate">{label}</span>
        <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={CHEVRON} />
        </svg>
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label={`Switch organisation. Current role ${role}`}
          className="absolute right-0 top-full z-40 mt-2 w-72 rounded-xl border border-gray-200 bg-white p-3 shadow-xl"
        >
          <SessionSwitchPanel includeRoles={false} />
        </div>
      ) : null}
    </div>
  );
}

/** Desktop bar. Hidden when this person has nothing to switch. */
export const SessionSwitchBar: React.FC = () => {
  const choices = useSessionChoices();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('click', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!choices.showRoles && !choices.showPlaces) return null;

  return (
    <div
      ref={rootRef}
      className="sticky top-0 z-40 hidden md:block bg-white border-b border-gray-200 print:hidden"
    >
      <div className="flex items-center justify-between gap-4 h-14 px-4 sm:px-6 lg:px-8">
        {choices.showRoles ? <RoleToggle /> : <span className="text-sm font-semibold text-gray-800">{choices.role}</span>}
        {choices.showPlaces ? (
          <PlaceMenu open={open} onToggle={() => setOpen(value => !value)} />
        ) : (
          <span className="text-sm font-medium text-gray-600 truncate">{choices.tenantName}</span>
        )}
      </div>
      {choices.error ? <p className="px-4 sm:px-6 lg:px-8 pb-3 -mt-1 text-xs text-red-600">{choices.error}</p> : null}
    </div>
  );
};

/** Compact control for the phone header. */
export const SessionSwitchButton: React.FC = () => {
  const choices = useSessionChoices();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const label = sessionContextLabel(choices.role, choices.tenantName, choices.siteName);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('click', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!choices.showRoles && !choices.showPlaces) {
    return <div className="w-9" />;
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Switch role or organisation. Current: ${label}`}
        disabled={choices.switching}
        onClick={() => setOpen(value => !value)}
        className="inline-flex max-w-[11rem] items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-shell-900 shadow-sm disabled:opacity-60"
      >
        <span className="truncate">{choices.role}</span>
        <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={CHEVRON} />
        </svg>
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Switch role or organisation"
          className="absolute right-0 top-full z-40 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white p-3 shadow-xl"
        >
          <SessionSwitchPanel />
        </div>
      ) : null}
    </div>
  );
};
